import { useState, useRef, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/integrated-auth";
import { authService } from "@/lib/firebase";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const CODE_LENGTH = 8;
const GROUP_SIZE = 4; // XXXX-XXXX

interface JoinLeagueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function JoinLeagueDialog({ open, onOpenChange }: JoinLeagueDialogProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const fullCode = code.join("");

  const resetCode = useCallback(() => {
    setCode(Array(CODE_LENGTH).fill(""));
    // Focus first input after reset
    setTimeout(() => inputRefs.current[0]?.focus(), 50);
  }, []);

  const handleInput = useCallback((index: number, value: string) => {
    // Handle paste of full code
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (cleaned.length > 1) {
      // Pasted multiple chars - fill from current position
      const newCode = [...code];
      for (let i = 0; i < cleaned.length && index + i < CODE_LENGTH; i++) {
        newCode[index + i] = cleaned[i];
      }
      setCode(newCode);
      const nextIndex = Math.min(index + cleaned.length, CODE_LENGTH - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (cleaned.length === 1) {
      const newCode = [...code];
      newCode[index] = cleaned;
      setCode(newCode);
      // Auto-advance
      if (index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  }, [code]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newCode = [...code];
      if (code[index]) {
        // Clear current cell
        newCode[index] = "";
        setCode(newCode);
      } else if (index > 0) {
        // Move back and clear
        newCode[index - 1] = "";
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === "Enter" && fullCode.length === CODE_LENGTH) {
      handleSubmit();
    }
  }, [code, fullCode]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (pasted.length > 0) {
      const newCode = Array(CODE_LENGTH).fill("");
      for (let i = 0; i < Math.min(pasted.length, CODE_LENGTH); i++) {
        newCode[i] = pasted[i];
      }
      setCode(newCode);
      const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  }, []);

  const joinLeagueMutation = useMutation({
    mutationFn: async (inviteCode: string) => {
      setIsLoading(true);

      const token = await authService.getIdToken();
      if (!token) {
        throw new Error("Authentication required. Please sign in and try again.");
      }

      const leagueResponse = await fetch(`/api/leagues/invite/${inviteCode}`);

      if (!leagueResponse.ok) {
        throw new Error("Invalid invite code");
      }

      const league = await leagueResponse.json();

      try {
        console.log(`Attempting to join league ${league.id}`);

        const response = await fetch(`/api/leagues/${league.id}/join`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ inviteCode }),
          credentials: "include"
        });

        console.log(`Join league response status: ${response.status}`);

        let responseBody;

        try {
          const responseText = await response.text();
          console.log(`Response text:`, responseText);

          try {
            if (responseText && responseText.trim() &&
                !responseText.trim().startsWith('<') &&
                !responseText.includes('<!DOCTYPE')) {
              responseBody = JSON.parse(responseText);
            } else if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
              console.warn('Received HTML response instead of JSON');
              responseBody = { success: response.ok, htmlResponse: true };
            }
          } catch (jsonError) {
            console.error('Error parsing JSON response:', jsonError);
          }

          if (!response.ok && !responseBody) {
            if (responseText.includes('<!DOCTYPE') || responseText.includes('<html')) {
              throw new Error('Server error - please try again');
            } else {
              throw new Error(`Failed to join league: ${responseText}`);
            }
          }

          if (responseBody && responseBody.alreadyMember) {
            return { league, alreadyMember: true };
          }

          if (!response.ok) {
            throw new Error(`Failed to join league: ${responseText}`);
          }
        } catch (textError) {
          console.error('Error reading response:', textError);
          if (!response.ok) {
            throw new Error(`Failed to join league: Network error`);
          }
        }

        const responseData = responseBody || {};
        return { league, success: true, data: responseData };
      } catch (error) {
        console.error("Error in join league mutation:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      if (data.alreadyMember) {
        toast({
          title: "Already a member",
          description: `You are already a member of "${data.league.name}".`,
        });
      } else {
        toast({
          title: "League joined!",
          description: `You have joined "${data.league.name}" successfully.`,
        });
      }

      resetCode();
      onOpenChange(false);
      setIsLoading(false);

      queryClient.invalidateQueries();
      navigate(`/leagues/${data.league.id}`);
    },
    onError: (error: any) => {
      console.error('Join league error:', error);

      if (error.code === 'MEMBER_LIMIT_REACHED') {
        toast({
          title: "League is full",
          description: `This league has reached its maximum capacity of ${error.limit} players.`,
          variant: "destructive",
        });
      } else if (error.message === "Already a member of this league") {
        toast({
          title: "Already a member",
          description: "You are already a member of this league.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error joining league",
          description: error.message || "There was an error joining the league.",
          variant: "destructive",
        });
      }

      setIsLoading(false);
    },
  });

  const handleSubmit = () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to join a league.",
        variant: "destructive",
      });
      return;
    }
    if (fullCode.length < 6) {
      toast({
        title: "Invalid code",
        description: "Invite code must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }
    joinLeagueMutation.mutate(fullCode);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetCode(); onOpenChange(v); }}>
      <DialogContent className="!inset-auto !left-4 !right-4 !top-[15%] !translate-x-0 !translate-y-0 !max-h-fit !rounded-lg !border max-w-sm mx-auto sm:!left-[50%] sm:!top-[50%] sm:!right-auto sm:!translate-x-[-50%] sm:!translate-y-[-50%]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Join a League</DialogTitle>
          <DialogDescription>
            Enter an invite code to join a private league.
          </DialogDescription>
        </DialogHeader>

        {/* Segmented code input */}
        <div className="flex items-center justify-center gap-1.5" onPaste={handlePaste}>
          {code.map((char, i) => (
            <div key={i} className="contents">
              <input
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                maxLength={2}
                value={char}
                onChange={(e) => handleInput(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
                className="w-9 h-11 text-center text-lg font-mono font-bold uppercase rounded-md border-2 border-muted-foreground/25 bg-background focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
              />
              {i === GROUP_SIZE - 1 && (
                <span className="text-xl font-bold text-muted-foreground mx-0.5">-</span>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button
            type="button"
            className="w-full bg-primary hover:bg-red-600 text-white"
            disabled={isLoading || joinLeagueMutation.isPending || fullCode.length < 6}
            onClick={handleSubmit}
          >
            {isLoading || joinLeagueMutation.isPending ? "Joining..." : "Join League"}
          </Button>
        </DialogFooter>

        {/* Help text - always visible */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
          <p className="font-medium text-blue-800 dark:text-blue-200 mb-1.5">
            Where to find invite codes:
          </p>
          <ul className="text-blue-700 dark:text-blue-300 text-xs space-y-1">
            <li>• Ask the league owner for the code</li>
            <li>• League owners can find it under <strong>⋮ → League Info</strong></li>
            <li>• Members may also share codes (if enabled by owner)</li>
            <li>• Or use an invite link shared with you</li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
